// -----------------------------------------------------------------------------
//
// This file is the copyrighted property of Tableau Software and is protected
// by registered patents and other applicable U.S. and international laws and
// regulations.
//
// Unlicensed use of the contents of this file is prohibited. Please refer to
// the NOTICES.txt file for further details.
//
// -----------------------------------------------------------------------------
import * as _ from 'underscore';
import { incrementRouterSpaceReactKey } from '../../app/actions/spaces/IncrementRouterSpaceReactKeyAction';
import { getAppStore } from '../../app/AppStore';
import { ExtractEncryptionState, IDataSourceInfo } from '../../ts/models/IDataSourceInfo';
import { IWorkbookInfo } from '../../ts/models/IWorkbookInfo';
import { IExtractItemInfo } from '../../ts/models/resourceInfoParts';
import { IPollExtractActionStatusRequestParams, IPollExtractActionStatusResult, PollExtractActionStatusRequest } from '../../ts/ServerApi/requests/PollExtractActionStatusRequest';
import { ServiceLocator } from '../../app/ServiceLocator';
import { RequestPoller } from './RequestPoller';
import { ExtractActionType } from '~src/extract/actions/CreateExtractTaskAction';
import { CreateMixedContentExtractTaskActionService } from '~src/extract/actions/CreateMixedContentExtractTaskAction';

export const extractActionStatusInterval = 10000; // Polling interval of 10 secs

let statusPoller: RequestPoller<IPollExtractActionStatusRequestParams, IPollExtractActionStatusResult>;

function pollAndUpdateExtractActionStatus(contentItem: IWorkbookInfo | IDataSourceInfo) {

  if (!(contentItem as IExtractItemInfo).extractCreationPending &&
  (contentItem as IExtractItemInfo).extractEncryptionState !== ExtractEncryptionState.pendingDecryption &&
  (contentItem as IExtractItemInfo).extractEncryptionState !== ExtractEncryptionState.pendingEncryption) {
  // no pending status, do nothing
    return;
  }

  let description: string;

  if (contentItem.extractCreationPending) {
    description = ExtractActionType.createExtracts;
  }
  else if (contentItem.extractEncryptionState === ExtractEncryptionState.pendingEncryption) {
    description = ExtractActionType.encryptExtract;
  }
  else {
    description = ExtractActionType.decryptExtract;
  }

  pollExtractActionStatus(contentItem, description);
}

  function completeExtractStatusPolling(result: IPollExtractActionStatusResult, contentItem: IWorkbookInfo | IDataSourceInfo, actionType: string) {
    if (isExtractActionStatusValid(result)) {
      CreateMixedContentExtractTaskActionService.pollExtractActionStatus(contentItem, actionType, result);
      if (!_.isUndefined(result.pending) && !result.pending) {
        getAppStore().dispatch(incrementRouterSpaceReactKey());
      }
    }
  }

  function isExtractActionStatusValid(data: IPollExtractActionStatusResult): boolean {
    if (data?.pending) {
      return false;
    }
    // This failed due to invalid data we want to cancel the polling
    cancelExtractActionStatusPolling();
    return true;
  }

  function pollExtractActionStatus(contentItem: IWorkbookInfo | IDataSourceInfo, actionType: string) {
    const request = new PollExtractActionStatusRequest({
      id: contentItem.id,
      contentType: contentItem.contentType,
      actionType: actionType
    });

    const isComplete = (result: IPollExtractActionStatusResult): boolean => {
      return result && !result.pending;
    };

    const successCallback = (result: IPollExtractActionStatusResult) => {
      completeExtractStatusPolling(result, contentItem, actionType);
    };

    statusPoller = new RequestPoller(
      request,
      isComplete,
      successCallback,
      null,
      null, // notifyCallback
      extractActionStatusInterval
    );
    statusPoller.startPolling();
  }

  function cancelExtractActionStatusPolling() {
    statusPoller?.cancelPolling();
  }

  export const ExtractActionStatusService = {
    pollAndUpdateExtractActionStatus
  };

export type ExtractActionStatusService = typeof ExtractActionStatusService;
